import React, { useState, useRef } from "react";

interface ColonyTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  shortcut?: string;
  delayMs?: number;
  /** Extra classes on the tooltip wrapper (e.g. "w-full" for sidebar) */
  className?: string;
}

export function ColonyTooltip({
  content,
  children,
  side = "top",
  shortcut,
  delayMs = 400,
  className,
}: ColonyTooltipProps) {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    timeoutRef.current = setTimeout(() => setVisible(true), delayMs);
  };

  const handleLeave = () => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
    left: "right-full top-1/2 -translate-y-1/2 mr-1.5",
    right: "left-full top-1/2 -translate-y-1/2 ml-1.5",
  };

  return (
    <div
      className={`relative inline-flex${className ? ` ${className}` : ""}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {visible && (
        <div
          className={`absolute z-50 pointer-events-none ${positionClasses[side]}`}
        >
          <div
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-[6px] whitespace-nowrap"
            style={{
              background: "var(--glass-bg)",
              backdropFilter: "var(--glass-blur)",
              WebkitBackdropFilter: "var(--glass-blur)",
              border: "1px solid var(--glass-border)",
              boxShadow: "var(--glass-shadow)",
              animation: "colony-fadeIn 120ms var(--ease-out-expo)",
            }}
          >
            <span className="text-[12px] text-colony-text-primary">{content}</span>
            {shortcut && (
              <span className="text-[10px] text-colony-text-tertiary font-mono px-1 py-0.5 rounded bg-[rgba(128,128,128,0.08)] border border-colony-border-subtle">
                {shortcut}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
